import type { WeeklyUpsertItem, WeeklyUpsertResult } from '../api/assignments';

/**
 * A cell of the entry grid waiting to be saved. When the server refused it because the
 * week changed since the grid was loaded, `conflict` carries what the week holds now:
 * the change stays pending until the user picks a side (keep theirs, or take the current one).
 */
export interface WeeklyWorkload {
  consultant_id: string;
  project_id: string;
  week_start: string;
  days: number; // 0-5 days per week
  /**
   * What the cell held on the server, as the user saw it when they first changed it. It is what
   * the save expects the week to still hold (`expected_weekly_load`), whatever the grid shows by
   * then: a draft taken up again after a colleague saved that week must conflict, not overwrite
   * (#55). Absent on a change that was not typed in the grid (the grid's value is then the base).
   */
  base_days?: number;
  conflict?: { current_days: number };
}

type CellRef = Pick<WeeklyWorkload, 'consultant_id' | 'project_id' | 'week_start'>;

export const cellKey = (cell: CellRef): string =>
  `${cell.consultant_id}-${cell.project_id}-${cell.week_start}`;

/**
 * Batch items for the changes to save. Each one carries the total its week had when the
 * user changed it (`base_days`, else the grid's value: `expected_weekly_load`), so the server
 * refuses it instead of silently overwriting a change someone else saved since. A change that
 * already conflicted starts from the current value the server reported: sending it again is the
 * user's informed choice.
 */
export const toBatchItems = (
  changes: WeeklyWorkload[],
  originalDays: (cell: CellRef) => number
): WeeklyUpsertItem[] =>
  changes.map(change => ({
    consultant_id: change.consultant_id,
    project_id: change.project_id,
    week_start: change.week_start,
    weekly_load: change.days,
    expected_weekly_load: change.conflict ? change.conflict.current_days : (change.base_days ?? originalDays(change)),
  }));

/**
 * The pending list after a batch save: what the server took leaves the list, everything
 * else stays. A refused-as-conflict change keeps the current value reported by the server;
 * any other refusal stays as typed. A change the batch did not carry is left untouched.
 */
export const reconcilePendingAfterSave = (
  pending: WeeklyWorkload[],
  results: WeeklyUpsertResult[]
): WeeklyWorkload[] => {
  const resultByCell = new Map(results.map(result => [cellKey(result), result]));

  return pending.flatMap((change): WeeklyWorkload[] => {
    const result = resultByCell.get(cellKey(change));
    if (!result) return [change];
    if (!result.error) return [];
    if (result.conflict) {
      return [{ ...change, conflict: { current_days: result.current_weekly_load ?? 0 } }];
    }
    return [change];
  });
};

// Returns the pending list with `change` applied: replaces the entry of the same
// (consultant, project, week), drops it when the value is back to the original one,
// or appends it. Always looks the entry up in the list it is given, so it can be chained
// inside one state update without relying on indexes from an older render.
// A cell in conflict stays in conflict while it is edited (the user has not picked a side yet).
// The value the cell held when it was first changed is kept as its `base_days` (#55), through the
// later edits of that cell.
export const applyWorkloadChange = (
  pending: WeeklyWorkload[],
  change: WeeklyWorkload,
  originalDays: number
): WeeklyWorkload[] => {
  const isSameCell = (c: WeeklyWorkload) =>
    c.consultant_id === change.consultant_id &&
    c.project_id === change.project_id &&
    c.week_start === change.week_start;
  const hasPending = pending.some(isSameCell);

  if (change.days === originalDays) {
    // Same as original: no change to keep
    return hasPending ? pending.filter(c => !isSameCell(c)) : pending;
  }
  return hasPending
    ? pending.map(c => (isSameCell(c) ? { ...change, conflict: c.conflict, base_days: c.base_days ?? originalDays } : c))
    : [...pending, { ...change, base_days: originalDays }];
};

/**
 * What a cell held in the pending list before « Répéter jusqu'à… » (#27) wrote it, and what
 * the repetition left there: enough to take it back without touching anything edited since.
 */
export interface RepeatUndoEntry {
  key: string;
  previous: WeeklyWorkload | null;
  applied: WeeklyWorkload | null;
}

/**
 * The pending list with a repetition written into it, one cell per week, exactly as if each
 * value had been typed (a week back at its saved value leaves the list), and what is needed
 * to undo it.
 */
export const applyRepeatToPending = (
  pending: WeeklyWorkload[],
  row: Pick<WeeklyWorkload, 'consultant_id' | 'project_id'>,
  changes: { weekStart: string; days: number }[],
  originalDays: (weekStart: string) => number
): { pending: WeeklyWorkload[]; undo: RepeatUndoEntry[] } => {
  let next = pending;
  const touched = changes.map(({ weekStart, days }) => {
    const cell = { ...row, week_start: weekStart };
    const key = cellKey(cell);
    const previous = next.find(change => cellKey(change) === key) ?? null;
    next = applyWorkloadChange(next, { ...cell, days }, originalDays(weekStart));
    return { key, previous };
  });
  const undo = touched.map(entry => ({
    ...entry,
    applied: next.find(change => cellKey(change) === entry.key) ?? null,
  }));
  return { pending: next, undo };
};

/**
 * Takes a repetition back: each cell goes back to what it held before, unless it was edited
 * (or saved) since, in which case it is left as it is now.
 */
export const undoRepeatOnPending = (
  pending: WeeklyWorkload[],
  undo: RepeatUndoEntry[]
): WeeklyWorkload[] => {
  let restored = pending;
  undo.forEach(({ key, previous, applied }) => {
    const now = restored.find(change => cellKey(change) === key) ?? null;
    if ((now?.days ?? null) !== (applied?.days ?? null)) return;
    restored = restored.filter(change => cellKey(change) !== key);
    if (previous) restored = [...restored, previous];
  });
  return restored;
};
